import React, { useEffect, useMemo, useReducer } from 'react'

import { GeolocationWatcher } from '../../components/TrackedJobs/GeolocationWatcher'
import { DecodedAccessToken, getStoredLocationToken, getStoredLogin, isRunningInWebview } from '../../graphql-client/storedState'
import { OpenJobCountWatcher } from './OpenJobCountWatcher'
import { reducer } from './reducers'
import { IDispatchContext, IState, IStateContext } from './types'
import { jwtDecode } from 'jwt-decode'
import { setCurrentUser } from './actions'
import { canSendMessageToWebView, sendMessageToWebView } from '../../util/react-native'
import { WebMessageType } from '../../util/react-native/types'
import { User, UserRole } from '../../common/types'
import { isExpired, onUserDataChanged } from '../../http-client'

const initialState: IState = {
  currentUser: null,
  offlineMode: false,
  unSavedJobCount: 0,
  showMessages: false,
  messageCount: 0,
  AlArcCodes: {
    components: [],
    towing_components: [],
    road_service_components: [],
    descriptions: [],
    completions: [],
  },
}

export const StateStore = React.createContext({} as IStateContext)
export const DispatchStore = React.createContext({} as IDispatchContext)

/** On page refresh or open, load user directly from local storage to avoid any unnecessary loading */
const getUserFromLocalStorage = () => {
  const token = getStoredLogin()
  if (!token) return null

  const decodedData = jwtDecode<DecodedAccessToken>(token)
  if (isExpired(decodedData.exp)) {
    return null
  }

  if (canSendMessageToWebView()) {
    sendMessageToWebView({ type: WebMessageType.LOGIN, data: JSON.stringify({ locationToken: getStoredLocationToken() }) })
  }

  return userFromToken(token)
}

/**Load user from local storage outside of react rendering cycle */
const initialUser = getUserFromLocalStorage()

interface StoreProviderProps {
  children: React.ReactNode
}
export const StoreProvider: React.FunctionComponent<StoreProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, { ...initialState, currentUser: initialUser })

  // Listen for user data changes when token is refreshed
  useEffect(() => {
    const unsubscribe = onUserDataChanged(newAccessToken => {
      const user = userFromToken(newAccessToken)
      dispatch(setCurrentUser(user))
    })
    return () => unsubscribe()
  }, [])

  const mfaPending = isMfaPending(state.currentUser)
  const isWebview = isRunningInWebview()
  const stateValue = useMemo(() => ({ state }), [state])

  return (
    <StateStore.Provider value={stateValue}>
      <DispatchStore.Provider value={{ dispatch }}>
        {state.currentUser && !mfaPending && <OpenJobCountWatcher />}
        {state.currentUser && !mfaPending && !isWebview && <GeolocationWatcher />}
        {/* {state.currentUser && <OfflineMode />} */}
        {children}
      </DispatchStore.Provider>
    </StateStore.Provider>
  )
}

export const isMfaPending = (user: User | null): boolean => {
  const mfaPendingStatuses = ['pending', 'sms_pending', 'pending_mfa_activation', 'pending_tupas_authentication']
  return !!user && mfaPendingStatuses.includes(user.mfa_status)
}

export function userFromToken(token: string) {
  const user = jwtDecode<DecodedAccessToken>(token)
  return userFromDecodedToken(user)
}

function userFromDecodedToken(decodedData: DecodedAccessToken): User {
  return {
    mfa_status: decodedData.mfa_status,
    role: decodedData.user_role as UserRole,
    username: decodedData.username,
    realName: decodedData.display_name,
    rememberMeForDays: decodedData.remember_me_for_days,
    user_id: decodedData.user_id,
    company_id: decodedData.company_id,
    exp: String(decodedData.exp),
  }
}
